import { Features } from '@backend/lib-feature-mgr';

import { findIndexableCityNames } from '~/business/City/getIndexableCityNames';
import { isIndexableName } from '~/business/IndexablePages/cacheIndexableNames';
import type { SearchJobOfferOutput } from '~/business/JobOffer/searchJobOffers';
import { Breadcrumbs } from '~/components/Breadcrumbs';
import { Head, NOINDEX_FOLLOW } from '~/components/Head';
import { LatestJobOffersCard } from '~/components/LatestJobOffersCard/LatestJobOffersCard';
import { SEOHeader } from '~/components/SEOHeader';
import { SEOContainer, SEOText, SEOTitle } from '~/components/SEOText';
import { featureGuard } from '~/guards/featureGuard';
import { useTranslation } from '~/hooks';
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs';
import { useGenerateSearchUrl } from '~/hooks/useGenerateSearchUrl';
import { useHubPageMeta } from '~/hooks/useHubPageMeta';
import { findCityBySlug } from '~/lists/cities';
import type { CityListItem } from '~/lists/types';
import { getHubJobOffers } from '~/utils/hubJobOffers';
import type { HubPagination } from '~/utils/hubPage';
import { getParamValue } from '~/utils/params';

interface CityPageProps {
  cityListItem: CityListItem;
  isIndexable: boolean;
  latestJobOffers: SearchJobOfferOutput[];
  pagination: HubPagination;
}

const CityPage = ({ cityListItem, isIndexable, latestJobOffers, pagination }: CityPageProps) => {
  const t = useTranslation();
  const title = useHubPageMeta(t('city_page.meta_title', { city: cityListItem.name }), pagination);
  const description = useHubPageMeta(t('city_page.meta_description', { city: cityListItem.name }), pagination);
  const generateSearchUrl = useGenerateSearchUrl();
  const breadcrumbs = useBreadcrumbs({ page: 'cities', subPages: [cityListItem.name] });

  return (
    <>
      <Head title={title} description={description} robots={isIndexable ? undefined : NOINDEX_FOLLOW} />
      <SEOHeader title={t('city_page.title', { city: cityListItem.name })} />
      <Breadcrumbs items={breadcrumbs} />
      <SEOContainer>
        <SEOTitle content={t('city_page.heading', { city: cityListItem.name })} />
        <SEOText content={cityListItem.description} />
      </SEOContainer>
      <LatestJobOffersCard
        latestJobOffers={latestJobOffers}
        seeAllOffersPath={generateSearchUrl({ address: cityListItem })}
        title={t('city_page.latest_job_offers', { city: cityListItem.name })}
        pagination={pagination}
      />
    </>
  );
};

export default CityPage;

export const getServerSideProps = featureGuard<CityPageProps>(Features.JobBoardSEO, async (businessUnit, context) => {
  const cityParam = getParamValue(context.params?.city)?.toLowerCase();

  if (!cityParam) {
    return { notFound: true };
  }

  const cityListItem = findCityBySlug(businessUnit, cityParam);

  if (!cityListItem) {
    return { notFound: true };
  }

  const [hubJobOffers, indexableCityNames] = await Promise.all([
    getHubJobOffers(businessUnit, context.query, { ...cityListItem.location }),
    findIndexableCityNames(businessUnit),
  ]);

  if (!hubJobOffers) {
    return { notFound: true };
  }

  return {
    props: {
      cityListItem,
      isIndexable: isIndexableName(indexableCityNames, cityListItem.name),
      ...hubJobOffers,
    },
  };
});
